import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE, WS_BASE } from '../config';
import {
  HighlightColor,
  SuggestOnEdit,
  COMPOSE_DRAFT_SUGGESTION_ID,
  encodeDocAnchor,
  mapIntentThroughComposeRestore,
  useReceiveOnlyDocumentSync,
  type SuggestionIntent,
} from '@narrativebench/editor-core';
import type { Editor } from '@tiptap/core';
import type * as Y from 'yjs';
import { DocumentHeader } from './DocumentHeader';
import { FloatingTocButton } from './FloatingTocButton';
import { FloatingToc } from './FloatingToc';
import { FormattingBar } from './FormattingBar';
import { CommentModal } from './CommentModal';
import { CommentSidebar, CommentSidebarProvider } from './CommentSidebar';
import { ExportDocument } from './ExportDocument';
import { SidebarInset } from './ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Node as ProseMirrorNode, Mark } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { captureInsertionContext, deferAfterDialogExit, ensureSuggestionMarkInDocument, findScrollContainer, highlightComments, recalculateCommentPosition, recalculateSuggestionPosition, restoreEditorTextSelection, scrollToSuggestionMark, syncSuggestionInsertMarkers, waitForDialogExit } from '../utils/editor-utils';
import { getDocumentPlainText } from '../utils/document-plain-text';
import { findActiveTocItemPos, findActiveTocItemPosAtDocPos, TOC_ACTIVE_VIEWPORT_OFFSET_PX, TOC_ACTIVE_VIEWPORT_SLOP_PX } from '../utils/document-section-navigation';
import { extractTocEntries, type TocEntry } from '../utils/extract-toc-entries';
import { useCommentHover } from '../hooks/use-comment-hover';
import { useSuggestionHover } from '../hooks/use-suggestion-hover';
import { usePersistedTocPinned } from '../hooks/usePersistedTocPinned';
import { APP_VIEWPORT_HEIGHT } from '../constants/layout';
import { CommentHoverCard } from './CommentHoverCard';
import { SuggestionHoverCard } from './SuggestionHoverCard';
import { createStaticEditorExtensions } from '../utils/editor-extensions';
import { applyDocumentApiContentToEditor, isUsableEditor } from '../utils/document-api-yjs';
import { DocumentLoadingPanel } from './DocumentLoadingPanel';
import { type Suggestion } from './SuggestionCard';
import { TextDiff } from './TextDiff';
import { Button } from './ui/button';
import type { MentionableEntity } from './RichTextField';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';

type Comment = {
  id: string;
  documentId: string;
  userId: string;
  userEmail?: string;
  content: string;
  fromPos: number;
  toPos: number;
  fromRel?: Record<string, unknown> | null;
  toRel?: Record<string, unknown> | null;
  selectedText: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
};

type ComposeSession = {
  mode: 'insert' | 'replace' | 'delete';
  originalText: string;
  apiFrom: number;
  apiTo: number;
  draftFrom: number;
  draftTo: number;
};

type CommittedSuggestionPayload = {
  originalText: string;
  suggestionText: string;
  fromPos: number;
  toPos: number;
  fromRel?: Record<string, unknown> | null;
  toRel?: Record<string, unknown> | null;
  contextBefore?: string | null;
  contextAfter?: string | null;
};

const COMPOSE_DRAFT_COLOR = 'rgba(34, 197, 94, 0.35)';

const isOwnPendingUserEdit = (suggestion: Suggestion, userId: string | undefined): boolean => {
  return (
    !!userId
    && suggestion.userId === userId
    && suggestion.issueType === 'user_edit'
    && suggestion.status === 'pending'
  );
};

const syncSuggestionHighlights = (editor: Editor, suggestions: Suggestion[]): void => {
  const markType = editor.state.schema.marks.highlightColor;
  if (!markType) {
    return;
  }

  const keepIds = new Set(suggestions.map((suggestion) => suggestion.id));
  const tr = editor.state.tr;
  let changed = false;

  editor.state.doc.descendants((node, pos) => {
    if (!node.isInline || !node.marks) {
      return;
    }
    node.marks.forEach((mark) => {
      if (mark.type.name !== 'highlightColor' || !mark.attrs.suggestionId) {
        return;
      }
      const suggestionId = String(mark.attrs.suggestionId);
      if (suggestionId === COMPOSE_DRAFT_SUGGESTION_ID || keepIds.has(suggestionId)) {
        return;
      }
      tr.removeMark(pos, pos + node.nodeSize, markType);
      changed = true;
    });
  });

  if (changed) {
    editor.view.dispatch(tr);
  }

  suggestions.forEach((suggestion) => {
    ensureSuggestionMarkInDocument(editor, suggestion, suggestions);
  });
  syncSuggestionInsertMarkers(editor, suggestions);
};

const findComposeDraftRanges = (editor: Editor): Array<{ from: number; to: number }> => {
  const ranges: Array<{ from: number; to: number }> = [];
  editor.state.doc.descendants((node, pos) => {
    if (!node.isInline || !node.marks) {
      return;
    }
    node.marks.forEach((mark) => {
      if (mark.type.name === 'highlightColor' && mark.attrs.suggestionId === COMPOSE_DRAFT_SUGGESTION_ID) {
        ranges.push({ from: pos, to: pos + node.nodeSize });
      }
    });
  });

  if (ranges.length === 0) {
    return [];
  }

  ranges.sort((left, right) => left.from - right.from);
  const merged: Array<{ from: number; to: number }> = [{ ...ranges[0]! }];
  for (let index = 1; index < ranges.length; index += 1) {
    const range = ranges[index]!;
    const last = merged[merged.length - 1]!;
    if (range.from <= last.to) {
      last.to = Math.max(last.to, range.to);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
};

const findComposeDraftRangeNear = (
  editor: Editor,
  from: number,
  to: number,
): { from: number; to: number } | null => {
  const overlapping = findComposeDraftRanges(editor).filter(
    (range) => range.from <= to && range.to >= from,
  );
  if (overlapping.length === 0) {
    return null;
  }
  return {
    from: Math.min(...overlapping.map((range) => range.from)),
    to: Math.max(...overlapping.map((range) => range.to)),
  };
};

const removeOrphanComposeDraftMarks = (
  editor: Editor,
  keepFrom: number,
  keepTo: number,
): void => {
  const markType = editor.state.schema.marks.highlightColor;
  if (!markType) {
    return;
  }

  const orphanRanges = findComposeDraftRanges(editor).filter(
    (range) => range.to < keepFrom || range.from > keepTo,
  );
  if (orphanRanges.length === 0) {
    return;
  }

  let tr = editor.state.tr;
  orphanRanges.forEach((range) => {
    tr = tr.removeMark(range.from, range.to, markType);
  });
  editor.view.dispatch(tr);
};

export const CommentOnlyDocument: React.FC = () => {
  const { projectId, documentId } = useParams<{ projectId?: string; documentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, loading: authLoading, user } = useAuth();
  const { t } = useLocale();
  const { showToast, showErrorToast } = useToast();
  const shareToken = searchParams.get('shareToken');
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [documentIcon, setDocumentIcon] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const [documentOwnerId, setDocumentOwnerId] = useState<string | null>(null);
  const [documentProjectId, setDocumentProjectId] = useState<string | null>(null);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tocHeadings, setTocHeadings] = useState<TocEntry[]>([]);
  const [tocParagraphs, setTocParagraphs] = useState<Array<{ text: string; pos: number }>>([]);
  const [isFloatingTocVisible, setIsFloatingTocVisible] = useState<boolean>(false);
  const [isTocPinned, setIsTocPinned] = usePersistedTocPinned(documentId);
  const [activeTocPos, setActiveTocPos] = useState<number | null>(null);
  const ignoreTocScrollActiveUpdatesRef = useRef<boolean>(false);
  const [draggedTocIndex, setDraggedTocIndex] = useState<number | null>(null);
  const [dragOverTocIndex, setDragOverTocIndex] = useState<number | null>(null);
  const [touchDraggedIndex, setTouchDraggedIndex] = useState<number | null>(null);
  const [touchDragOverIndex, setTouchDragOverIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [deletedComments, setDeletedComments] = useState<Comment[]>([]);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [isCreatingComment, setIsCreatingComment] = useState<boolean>(false);
  const [selectedTextForComment, setSelectedTextForComment] = useState<string>('');
  const [selectedRangeForComment, setSelectedRangeForComment] = useState<{ from: number; to: number } | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [composeSession, setComposeSession] = useState<ComposeSession | null>(null);
  const [composeDraftText, setComposeDraftText] = useState<string>('');
  const [composeToolbarPosition, setComposeToolbarPosition] = useState<{ left: number; top: number } | null>(null);
  const [isCreatingSuggestion, setIsCreatingSuggestion] = useState<boolean>(false);
  const [isExportOpen, setIsExportOpen] = useState<boolean>(false);
  const tocListRef = useRef<HTMLUListElement | null>(null);
  const composeToolbarRef = useRef<HTMLDivElement | null>(null);
  const composeActiveRef = useRef(false);
  const composeSnapshotRef = useRef<Record<string, unknown> | null>(null);
  const composeSessionRef = useRef<ComposeSession | null>(null);
  const ydocRef = useRef<Y.Doc | null>(null);
  const handlingSuggestionIntentRef = useRef(false);
  const startInlineComposeRef = useRef<(intent: SuggestionIntent) => void>(() => {});
  const handleSuggestionIntentRef = useRef<(intent: SuggestionIntent) => void>(() => {});
  const commentsRef = useRef<Comment[]>([]);
  const suggestionsRef = useRef<Suggestion[]>([]);
  const editorRef = useRef<Editor | null>(null);
  const isMobile = useIsMobile();

  const editor = useEditor({
    extensions: [
      ...createStaticEditorExtensions(HighlightColor),
      SuggestOnEdit.configure({
        getComposeDraftRange: () => {
          if (!composeActiveRef.current || !composeSessionRef.current) {
            return null;
          }
          const session = composeSessionRef.current;
          const ed = editorRef.current;
          if (ed) {
            const markRange = findComposeDraftRangeNear(ed, session.draftFrom, session.draftTo);
            if (markRange) {
              return markRange;
            }
          }
          return { from: session.draftFrom, to: session.draftTo };
        },
        onSuggestionIntent: (intent: SuggestionIntent) => {
          handleSuggestionIntentRef.current(intent);
        },
      }),
    ],
    editable: true,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[400px]',
      },
    },
  });
  editorRef.current = editor;
  commentsRef.current = comments;
  suggestionsRef.current = suggestions;

  const reapplyDocumentMarksFromRefs = useCallback((): void => {
    const ed = editorRef.current;
    if (!ed) {
      return;
    }
    highlightComments(ed, commentsRef.current);
    suggestionsRef.current.forEach((suggestion) => {
      ensureSuggestionMarkInDocument(ed, suggestion, suggestionsRef.current);
    });
    syncSuggestionInsertMarkers(ed, suggestionsRef.current);
  }, []);

  const {
    hoveredComment,
    hoveredCommentPosition,
    commentHoverCardContentRef,
    setHoveredComment,
    closeCommentHover,
    setKeepCommentHoverOpen,
    onCommentHoverCardMouseEnter,
    onCommentHoverCardMouseLeave,
  } = useCommentHover(editor, comments, isMobile);

  const {
    hoveredSuggestion,
    hoveredSuggestionPosition,
    suggestionHoverCardContentRef,
    closeSuggestionHover,
    onSuggestionHoverCardMouseEnter,
    onSuggestionHoverCardMouseLeave,
  } = useSuggestionHover(editor, suggestions, isMobile);

  const getIssueTypeLabel = useCallback((issueType: string): string => {
    if (issueType === 'user_edit') {
      return t('suggestion_issue_type_user_edit');
    }
    const label = t(issueType);
    return label === issueType ? issueType : label;
  }, [t]);

  const canDismissSuggestion = useCallback((suggestion: Suggestion): boolean => {
    return isOwnPendingUserEdit(suggestion, user?.id);
  }, [user?.id]);

  const scrollToSuggestion = useCallback((target: Suggestion): void => {
    if (!editor) {
      return;
    }
    scrollToSuggestionMark(editor, target, undefined, suggestions);
  }, [editor, suggestions]);

  useEffect(() => {
    if (!authLoading && !token && !shareToken) {
      navigate('/login');
    }
  }, [authLoading, token, shareToken, navigate]);

  const loadComments = useCallback(async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/comments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const loadedComments = data.comments || [];
        
        if (editor && loadedComments.length > 0) {
          const recalculatedComments = loadedComments.map((comment: Comment) => {
            const recalculated = recalculateCommentPosition(editor, comment, ydocRef.current);
            return {
              ...comment,
              fromPos: recalculated.fromPos,
              toPos: recalculated.toPos,
            };
          });
          setComments(recalculatedComments);
        } else {
          setComments(loadedComments);
        }
      } else {
        console.error('Failed to load comments');
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  }, [token, documentId, editor]);

  const loadDeletedComments = useCallback(async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/comments?includeDeleted=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const loaded = (data.comments || []) as Comment[];
        if (editor && loaded.length > 0) {
          const recalculated = loaded.map((comment: Comment) => {
            const recalculatedPos = recalculateCommentPosition(editor, comment, ydocRef.current);
            return {
              ...comment,
              fromPos: recalculatedPos.fromPos,
              toPos: recalculatedPos.toPos,
            };
          });
          setDeletedComments(recalculated);
        } else {
          setDeletedComments(loaded);
        }
      } else {
        console.error('Failed to load deleted comments');
      }
    } catch (err) {
      console.error('Error loading deleted comments:', err);
    }
  }, [token, documentId, editor]);

  const loadSuggestions = useCallback(async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/suggestions/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        console.error('Failed to load suggestions');
        return;
      }

      const data = await response.json();
      const loadedSuggestions = Array.isArray(data.suggestions) ? data.suggestions as Suggestion[] : [];
      setSuggestions(
        loadedSuggestions.filter((suggestion) => suggestion.status === 'pending'),
      );
    } catch (err) {
      console.error('Error loading suggestions:', err);
    }
  }, [token, documentId]);

  const syncCommentLists = useCallback(async (): Promise<void> => {
    await loadComments();
    await loadDeletedComments();
  }, [loadComments, loadDeletedComments]);

  const loadSuggestionsRef = useRef(loadSuggestions);
  const syncCommentListsRef = useRef(syncCommentLists);
  loadSuggestionsRef.current = loadSuggestions;
  syncCommentListsRef.current = syncCommentLists;

  useReceiveOnlyDocumentSync({
    documentId: documentId || 'new',
    token: token || '',
    websocketUrl: WS_BASE,
    editor,
    enabled: Boolean(token && !shareToken && documentId && documentId !== 'new' && !isLoading && !error),
    pauseApplying: composeSession !== null,
    onRemoteContentApplied: reapplyDocumentMarksFromRefs,
    onStateless: (payload: string) => {
      if (payload === 'suggestions-changed') {
        void loadSuggestionsRef.current();
      }
      if (payload === 'comments-changed') {
        void syncCommentListsRef.current();
      }
    },
    clearLocalCacheOnStart: true,
    ydocRef,
  });

  const updateComposeToolbarPosition = useCallback((ed: Editor, from: number, to: number): void => {
    try {
      const startPos = ed.view.coordsAtPos(from);
      const endPos = ed.view.coordsAtPos(Math.max(from, to));
      const selectionCenter = (startPos.left + endPos.right) / 2;
      const panelHeight = composeToolbarRef.current?.offsetHeight ?? 160;
      const topBelow = endPos.bottom + 10;
      const topAbove = startPos.top - panelHeight - 10;
      const top = topBelow + panelHeight > window.innerHeight - 8
        ? Math.max(8, topAbove)
        : topBelow;
      const toolbarWidth = composeToolbarRef.current?.offsetWidth ?? 420;
      const padding = 10;
      const halfWidth = toolbarWidth / 2;
      const minCenter = padding + halfWidth;
      const maxCenter = window.innerWidth - padding - halfWidth;
      const left = Math.min(Math.max(selectionCenter, minCenter), Math.max(minCenter, maxCenter));
      setComposeToolbarPosition({ left, top });
    } catch {
      setComposeToolbarPosition(null);
    }
  }, []);

  const applyComposeDraftMark = useCallback((ed: Editor, from: number, to: number, caretPos?: number): void => {
    const markType = ed.state.schema.marks.highlightColor;
    if (!markType) {
      return;
    }
    const mark = markType.create({
      color: COMPOSE_DRAFT_COLOR,
      suggestionId: COMPOSE_DRAFT_SUGGESTION_ID,
    });
    let tr = ed.state.tr;
    if (from < to) {
      tr = tr.addMark(from, to, mark);
    }
    const selectionPos = Math.min(Math.max(caretPos ?? to, 0), tr.doc.content.size);
    tr = tr.setSelection(TextSelection.create(tr.doc, selectionPos)).setStoredMarks([mark]);
    ed.view.dispatch(tr);
  }, []);

  const readComposeSuggestionText = useCallback((ed: Editor, session: ComposeSession): string => {
    const from = session.draftFrom;
    const to = session.draftTo;
    if (from >= to) {
      return '';
    }
    return ed.state.doc.textBetween(from, to, '\n');
  }, []);

  const reapplyDocumentMarks = useCallback((ed: Editor): void => {
    highlightComments(ed, commentsRef.current);
    suggestionsRef.current.forEach((suggestion) => {
      ensureSuggestionMarkInDocument(ed, suggestion, suggestionsRef.current);
    });
  }, []);

  const restoreComposeSnapshot = useCallback((selectionPos?: number | null): void => {
    const ed = editorRef.current;
    const snapshot = composeSnapshotRef.current;
    composeActiveRef.current = false;
    composeSnapshotRef.current = null;
    composeSessionRef.current = null;
    if (ed && snapshot) {
      ed.commands.setContent(snapshot, { emitUpdate: false });
      reapplyDocumentMarks(ed);
      if (selectionPos != null) {
        const docSize = ed.state.doc.content.size;
        const pos = Math.max(0, Math.min(selectionPos, docSize));
        ed.chain().setTextSelection(pos).focus().run();
      }
    }
    setComposeSession(null);
    setComposeDraftText('');
    setComposeToolbarPosition(null);
  }, [reapplyDocumentMarks]);

  const commitComposeAndRestore = useCallback((): CommittedSuggestionPayload | null => {
    const ed = editorRef.current;
    const session = composeSessionRef.current;
    if (!ed || !session) {
      restoreComposeSnapshot();
      return null;
    }

    const markRange = findComposeDraftRangeNear(ed, session.draftFrom, session.draftTo);
    const draftFrom = markRange?.from ?? session.draftFrom;
    const draftTo = markRange?.to ?? session.draftTo;
    const suggestionText = draftFrom < draftTo
      ? ed.state.doc.textBetween(draftFrom, draftTo, '\n')
      : '';
    const payload: CommittedSuggestionPayload | null = (session.mode === 'delete' || suggestionText)
      ? {
          originalText: session.originalText,
          suggestionText,
          fromPos: session.apiFrom,
          toPos: session.apiTo,
        }
      : null;

    const insertPos = session.apiFrom;
    restoreComposeSnapshot(insertPos);
    if (payload && payload.fromPos === payload.toPos && !payload.originalText) {
      const context = captureInsertionContext(ed, insertPos);
      if (context) {
        payload.contextBefore = context.contextBefore;
        payload.contextAfter = context.contextAfter;
        const anchored = recalculateSuggestionPosition(ed, {
          originalText: '',
          fromPos: payload.fromPos,
          toPos: payload.toPos,
          contextBefore: context.contextBefore,
          contextAfter: context.contextAfter,
        }, ydocRef.current);
        if (anchored) {
          payload.fromPos = anchored.from;
          payload.toPos = anchored.to;
        }
      }
    }
    if (payload) {
      const anchor = encodeDocAnchor(ed, ydocRef.current, payload.fromPos, payload.toPos);
      if (anchor) {
        payload.fromRel = anchor.fromRel;
        payload.toRel = anchor.toRel;
      }
    }
    return payload;
  }, [restoreComposeSnapshot]);

  const postCommittedSuggestion = useCallback(async (
    payload: CommittedSuggestionPayload,
    options?: { showSuccessToast?: boolean },
  ): Promise<boolean> => {
    if (!token || !documentId || documentId === 'new') {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE}/api/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          documentId,
          originalText: payload.originalText,
          suggestionText: payload.suggestionText,
          fromPos: payload.fromPos,
          toPos: payload.toPos,
          fromRel: payload.fromRel ?? null,
          toRel: payload.toRel ?? null,
          contextBefore: payload.contextBefore ?? null,
          contextAfter: payload.contextAfter ?? null,
        }),
      });

      if (!response.ok) {
        showErrorToast(t('failed_to_create_suggestion'));
        return false;
      }

      const data = await response.json();
      const createdSuggestion = data.suggestion as Suggestion | undefined;
      if (createdSuggestion && createdSuggestion.status === 'pending') {
        setSuggestions((prev) => [
          {
            ...createdSuggestion,
            userEmail: createdSuggestion.userEmail || user?.email,
          },
          ...prev.filter((suggestion) => suggestion.id !== createdSuggestion.id),
        ]);
      } else {
        await loadSuggestions();
      }

      if (options?.showSuccessToast) {
        showToast(t('commenter_suggestion_created'));
      }
      return true;
    } catch (err) {
      console.error('Error creating suggestion:', err);
      showErrorToast(t('failed_to_create_suggestion'));
      return false;
    }
  }, [token, documentId, showToast, showErrorToast, t, user?.email, loadSuggestions]);

  const cancelCompose = useCallback((): void => {
    if (!composeActiveRef.current) {
      return;
    }
    const caretPos = composeSessionRef.current?.apiFrom ?? null;
    restoreComposeSnapshot(caretPos);
  }, [restoreComposeSnapshot]);

  const startInlineCompose = useCallback((intent: SuggestionIntent): void => {
    const ed = editorRef.current;
    if (!ed) {
      return;
    }
    if (composeActiveRef.current) {
      return;
    }

    const docSize = ed.state.doc.content.size;
    const from = Math.max(0, Math.min(intent.from, docSize));
    const to = Math.max(from, Math.min(intent.to, docSize));
    const suggestionText = intent.suggestionText;
    const originalText = from === to
      ? ''
      : (intent.originalText || ed.state.doc.textBetween(from, to, '\n'));

    composeSnapshotRef.current = ed.getJSON() as Record<string, unknown>;
    composeActiveRef.current = true;

    try {
      const mode: ComposeSession['mode'] = !originalText
        ? 'insert'
        : !suggestionText
          ? 'delete'
          : 'replace';

      ed.chain().focus().deleteRange({ from, to }).run();

      let draftFrom = from;
      let draftTo = from;
      if (suggestionText) {
        ed.chain().focus().insertContentAt(from, suggestionText).run();
        draftTo = ed.state.selection.from;
      }
      applyComposeDraftMark(ed, draftFrom, draftTo, draftTo);

      const markedRange = findComposeDraftRangeNear(ed, draftFrom, Math.max(draftFrom, draftTo));
      if (markedRange) {
        draftFrom = markedRange.from;
        draftTo = markedRange.to;
      }
      removeOrphanComposeDraftMarks(ed, draftFrom, draftTo);

      const session: ComposeSession = {
        mode,
        originalText,
        apiFrom: from,
        apiTo: to,
        draftFrom,
        draftTo,
      };
      composeSessionRef.current = session;
      setComposeSession(session);
      setComposeDraftText(suggestionText || '');
      updateComposeToolbarPosition(ed, draftFrom, draftTo);
    } catch (error) {
      console.error('Error starting suggestion compose:', error);
      composeActiveRef.current = false;
      composeSnapshotRef.current = null;
      composeSessionRef.current = null;
      setComposeSession(null);
      setComposeDraftText('');
      setComposeToolbarPosition(null);
    }
  }, [applyComposeDraftMark, updateComposeToolbarPosition]);

  const handleSuggestionIntent = useCallback((intent: SuggestionIntent): void => {
    if (handlingSuggestionIntentRef.current) {
      return;
    }

    handlingSuggestionIntentRef.current = true;
    try {
      if (composeActiveRef.current && !composeSessionRef.current) {
        composeActiveRef.current = false;
      }

      if (composeActiveRef.current && composeSessionRef.current) {
        const session = composeSessionRef.current;
        const ed = editorRef.current;
        const markRange = ed
          ? findComposeDraftRangeNear(ed, session.draftFrom, session.draftTo)
          : null;
        const mappedIntent = mapIntentThroughComposeRestore(intent, {
          from: markRange?.from ?? session.draftFrom,
          to: markRange?.to ?? session.draftTo,
          apiFrom: session.apiFrom,
          apiTo: session.apiTo,
        });
        const payload = commitComposeAndRestore();
        if (payload) {
          void postCommittedSuggestion(payload);
        }
        startInlineCompose(mappedIntent);
        return;
      }

      startInlineCompose(intent);
    } finally {
      queueMicrotask(() => {
        handlingSuggestionIntentRef.current = false;
      });
    }
  }, [commitComposeAndRestore, postCommittedSuggestion, startInlineCompose]);

  startInlineComposeRef.current = startInlineCompose;
  handleSuggestionIntentRef.current = handleSuggestionIntent;

  const submitSuggestion = useCallback(async (): Promise<void> => {
    const session = composeSessionRef.current;
    const ed = editorRef.current;
    if (!token || !documentId || documentId === 'new' || !session || !ed) {
      return;
    }

    const suggestionText = readComposeSuggestionText(ed, session);

    if (session.mode !== 'delete' && !suggestionText) {
      showErrorToast(t('failed_to_create_suggestion'));
      return;
    }

    setIsCreatingSuggestion(true);
    try {
      const payload = commitComposeAndRestore();
      if (!payload) {
        return;
      }
      await postCommittedSuggestion(payload, { showSuccessToast: true });
    } finally {
      setIsCreatingSuggestion(false);
    }
  }, [token, documentId, showToast, showErrorToast, t, readComposeSuggestionText, commitComposeAndRestore, postCommittedSuggestion]);

  const dismissSuggestion = useCallback(async (suggestion: Suggestion): Promise<void> => {
    if (!token || !isOwnPendingUserEdit(suggestion, user?.id)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/suggestions/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ suggestionId: suggestion.id }),
      });

      if (!response.ok) {
        showErrorToast(t('failed_to_dismiss_suggestion'));
        return;
      }

      setSuggestions((prev) => prev.filter((currentSuggestion) => currentSuggestion.id !== suggestion.id));
      await loadSuggestions();
    } catch (err) {
      console.error('Error dismissing suggestion:', err);
      showErrorToast(t('failed_to_dismiss_suggestion'));
    }
  }, [token, showToast, showErrorToast, t, loadSuggestions, user?.id]);

  useEffect(() => {
    if (!editor || !composeSession) {
      return;
    }

    const syncComposeDraft = ({ transaction }: { transaction: { docChanged: boolean; mapping: { map: (pos: number, assoc?: number) => number } } }): void => {
      if (!composeActiveRef.current) {
        return;
      }

      const current = composeSessionRef.current;
      if (!current) {
        return;
      }

      let draftFrom = current.draftFrom;
      let draftTo = current.draftTo;
      if (transaction.docChanged) {
        draftFrom = transaction.mapping.map(draftFrom, -1);
        draftTo = transaction.mapping.map(draftTo, 1);
      }

      if (draftTo < draftFrom) {
        draftTo = draftFrom;
      }

      const markRange = findComposeDraftRangeNear(editor, draftFrom, draftTo);
      if (markRange) {
        draftFrom = markRange.from;
        draftTo = markRange.to;
      }

      const nextSession = { ...current, draftFrom, draftTo };
      composeSessionRef.current = nextSession;

      if (transaction.docChanged) {
        const nextDraftText = readComposeSuggestionText(editor, nextSession);
        setComposeDraftText((previous) => (previous === nextDraftText ? previous : nextDraftText));
      }

      updateComposeToolbarPosition(editor, draftFrom, Math.max(draftFrom, draftTo));
    };

    const syncToolbarFromSelection = (): void => {
      const current = composeSessionRef.current;
      if (!current) {
        return;
      }
      updateComposeToolbarPosition(
        editor,
        current.draftFrom,
        Math.max(current.draftFrom, current.draftTo),
      );
    };

    editor.on('transaction', syncComposeDraft);
    editor.on('selectionUpdate', syncToolbarFromSelection);
    window.addEventListener('scroll', syncToolbarFromSelection, true);
    window.addEventListener('resize', syncToolbarFromSelection);
    return () => {
      editor.off('transaction', syncComposeDraft);
      editor.off('selectionUpdate', syncToolbarFromSelection);
      window.removeEventListener('scroll', syncToolbarFromSelection, true);
      window.removeEventListener('resize', syncToolbarFromSelection);
    };
  }, [editor, composeSession !== null, updateComposeToolbarPosition, readComposeSuggestionText]);

  useLayoutEffect(() => {
    if (!composeSession || !composeToolbarPosition || !editor) {
      return;
    }
    const toolbarWidth = composeToolbarRef.current?.offsetWidth;
    if (!toolbarWidth) {
      return;
    }
    const padding = 10;
    const halfWidth = toolbarWidth / 2;
    const minCenter = padding + halfWidth;
    const maxCenter = window.innerWidth - padding - halfWidth;
    const clampedLeft = Math.min(Math.max(composeToolbarPosition.left, minCenter), Math.max(minCenter, maxCenter));
    if (Math.abs(clampedLeft - composeToolbarPosition.left) > 0.5) {
      setComposeToolbarPosition((current) => (current ? { ...current, left: clampedLeft } : current));
    }
  }, [composeSession, composeDraftText, composeToolbarPosition, editor]);

  useEffect(() => {
    if (!composeSession) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancelCompose();
        return;
      }

      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        event.stopPropagation();
        if (!isCreatingSuggestion) {
          void submitSuggestion();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [composeSession, cancelCompose, submitSuggestion, isCreatingSuggestion]);

  const handleHighlightComments = useCallback((): void => {
    highlightComments(editor, comments);
  }, [editor, comments]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    if (editor) {
      const timeoutId = setTimeout(() => {
        handleHighlightComments();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [editor, comments, handleHighlightComments]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const timeoutId = setTimeout(() => {
      syncSuggestionHighlights(editor, suggestions);
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [editor, suggestions]);

  useEffect(() => {
    const loadDocument = async (): Promise<void> => {
      if (authLoading || !isUsableEditor(editor)) {
        return;
      }

      if (!documentId || documentId === 'new') {
        setIsLoading(false);
        return;
      }

      if (shareToken) {
        setIsLoading(true);
        setError(null);

        try {
          const response = await fetch(`${API_BASE}/api/share-links/${shareToken}/document`);

          if (!response.ok) {
            if (response.status === 404) {
              setError(t('document_not_found') || 'Document not found');
            } else {
              setError(t('failed_to_load_document') || 'Failed to load document');
            }
            setIsLoading(false);
            return;
          }

          const data = await response.json();

          setDocumentTitle(data.title || '');
          setDocumentIcon(data.icon || null);
          setOwnerEmail(data.ownerEmail || null);
          setDocumentOwnerId(data.ownerId || null);

          applyDocumentApiContentToEditor(data, editorRef.current);

          setIsLoading(false);
        } catch (err) {
          console.error('Error loading document via share token:', err);
          setError(t('failed_to_load_document') || 'Failed to load document');
          setIsLoading(false);
        }
        return;
      }

      if (!token) {
        setError(t('no_permission') || "You don't have permission to access this page.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const documentUrl = projectId && projectId !== 'new'
          ? `${API_BASE}/api/documents/${documentId}?contextProjectId=${encodeURIComponent(projectId)}`
          : `${API_BASE}/api/documents/${documentId}`;
        const response = await fetch(documentUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            setError(t('no_permission') || "You don't have permission to access this page.");
          } else if (response.status === 404) {
            setError(t('document_not_found') || 'Document not found');
          } else {
            setError(t('failed_to_load_document') || 'Failed to load document');
          }
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        if (!data.sharePermission || data.sharePermission !== 'commenter') {
          navigate(projectId ? `/projects/${projectId}/documents/${documentId}` : `/documents/${documentId}`);
          return;
        }

        setDocumentTitle(data.title || '');
        setDocumentIcon(data.icon || null);
        setOwnerEmail(data.ownerEmail || null);
        setDocumentOwnerId(data.ownerId || null);

        const headerProjectId = projectId || data.projectId;
        setDocumentProjectId(headerProjectId || null);
        if (headerProjectId) {
          try {
            const projectResponse = await fetch(`${API_BASE}/api/projects/${headerProjectId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            if (projectResponse.ok) {
              const projectData = await projectResponse.json();
              setProjectTitle(projectData.title || '');
            }
          } catch (err) {
            console.error('Error fetching project title:', err);
          }
        }

        applyDocumentApiContentToEditor(data, editorRef.current);

        setIsLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(t('failed_to_load_document') || 'Failed to load document');
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [token, documentId, editor, projectId, shareToken, t, navigate, authLoading]);

  useEffect(() => {
    if (!token || !documentProjectId) {
      setMentionableEntities([]);
      return;
    }

    const fetchMentionableEntities = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${documentProjectId}/mentionable-entities`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch (err) {
        console.error('Error fetching mentionable entities:', err);
        setMentionableEntities([]);
      }
    };

    void fetchMentionableEntities();
  }, [token, documentProjectId]);

  useEffect(() => {
    if (!editor) return;

    const activeSuggestionIds = new Set(suggestions.map((suggestion) => suggestion.id));
    const clearNonCommentHighlights = (): void => {
      const { state } = editor;
      const markType = state.schema.marks.highlightColor;
      if (!markType) return;

      const tr = state.tr;
      let hasChanges = false;

      const rangesToClear: Array<{ from: number; to: number }> = [];

      state.doc.descendants((node: ProseMirrorNode, pos: number) => {
        if (node.isInline && node.marks) {
          node.marks.forEach((mark: Mark) => {
            if (mark.type.name === 'highlightColor') {
              const attrs = mark.attrs;
              const isStaleSuggestion = typeof attrs.suggestionId === 'string' && !activeSuggestionIds.has(attrs.suggestionId);
              if ((!attrs.commentId && !attrs.suggestionId) || isStaleSuggestion) {
                const textLength = node.textContent?.length || 0;
                rangesToClear.push({ from: pos, to: pos + textLength });
                hasChanges = true;
              }
            }
          });
        }
      });

      rangesToClear.forEach(({ from, to }) => {
        tr.removeMark(from, to, markType);
      });

      if (hasChanges) {
        editor.view.dispatch(tr);
      }
    };

    const handleUpdate = (): void => {
      clearNonCommentHighlights();
    };

    const timeoutId = setTimeout(() => {
      clearNonCommentHighlights();
    }, 100);

    editor.on('update', handleUpdate);

    return () => {
      clearTimeout(timeoutId);
      editor.off('update', handleUpdate);
    };
  }, [editor, documentTitle, suggestions]);

  useEffect(() => {
    if (!editor) return;

    const extractHeadingsAndParagraphs = (): void => {
      const { headings, paragraphs } = extractTocEntries(editor.state.doc);
      setTocHeadings(headings);
      setTocParagraphs(paragraphs);
    };

    extractHeadingsAndParagraphs();
    if (isFloatingTocVisible || isTocPinned) {
      editor.on('update', extractHeadingsAndParagraphs);
      return () => {
        editor.off('update', extractHeadingsAndParagraphs);
      };
    }
    return;
  }, [editor, isFloatingTocVisible, isTocPinned]);

  useEffect(() => {
    if (!editor || (!isFloatingTocVisible && !isTocPinned)) {
      return;
    }

    const getTocItems = () => (tocHeadings.length > 0 ? tocHeadings : tocParagraphs);

    const updateActiveTocPosFromScroll = (): void => {
      if (ignoreTocScrollActiveUpdatesRef.current) {
        return;
      }
      const scrollContainer = findScrollContainer(editor.view.dom.parentElement);
      const viewportTop = (scrollContainer?.getBoundingClientRect().top ?? 0) + TOC_ACTIVE_VIEWPORT_OFFSET_PX;
      setActiveTocPos(
        findActiveTocItemPos(
          getTocItems(),
          (pos) => {
            try {
              return editor.view.coordsAtPos(Math.min(pos + 1, editor.state.doc.content.size)).top;
            } catch {
              return null;
            }
          },
          viewportTop,
          TOC_ACTIVE_VIEWPORT_SLOP_PX,
        ),
      );
    };

    const finishProgrammaticTocScroll = (): void => {
      ignoreTocScrollActiveUpdatesRef.current = false;
      updateActiveTocPosFromScroll();
    };

    const updateActiveTocPosFromSelection = (): void => {
      setActiveTocPos(findActiveTocItemPosAtDocPos(getTocItems(), editor.state.selection.from));
    };

    updateActiveTocPosFromScroll();
    const scrollContainer = findScrollContainer(editor.view.dom.parentElement);
    scrollContainer?.addEventListener('scroll', updateActiveTocPosFromScroll, { passive: true });
    scrollContainer?.addEventListener('scrollend', finishProgrammaticTocScroll);
    editor.on('selectionUpdate', updateActiveTocPosFromSelection);

    return () => {
      scrollContainer?.removeEventListener('scroll', updateActiveTocPosFromScroll);
      scrollContainer?.removeEventListener('scrollend', finishProgrammaticTocScroll);
      editor.off('selectionUpdate', updateActiveTocPosFromSelection);
    };
  }, [editor, isFloatingTocVisible, isTocPinned, tocHeadings, tocParagraphs]);


  const scrollToTocPosition = (pos: number): void => {
    if (!editor) return;

    ignoreTocScrollActiveUpdatesRef.current = true;
    setActiveTocPos(pos);

    const { view } = editor;
    const coords = view.coordsAtPos(pos + 1);
    const scrollContainer = findScrollContainer(view.dom.parentElement);

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetY = coords.top - containerRect.top + scrollContainer.scrollTop - TOC_ACTIVE_VIEWPORT_OFFSET_PX;
      scrollContainer.scrollTo({
        top: targetY,
        behavior: 'smooth',
      });
      window.setTimeout(() => {
        if (ignoreTocScrollActiveUpdatesRef.current) {
          ignoreTocScrollActiveUpdatesRef.current = false;
        }
      }, 600);
    } else {
      window.scrollTo({
        top: coords.top + window.scrollY - TOC_ACTIVE_VIEWPORT_OFFSET_PX,
        behavior: 'smooth',
      });
      ignoreTocScrollActiveUpdatesRef.current = false;
    }
  };


  const moveTocSection = (): void => {
    // No-op in comment-only mode
  };

  if (authLoading || isLoading) {
    return <DocumentLoadingPanel />;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</div>
        <button
          onClick={() => navigate('/desk')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--scribane-black)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          {t('back_to_desk') || 'Back to Desk'}
        </button>
      </div>
    );
  }

  const toggleExport = (): void => {
    setIsExportOpen((previous) => !previous);
  };

  const closeExport = (): void => {
    setIsExportOpen(false);
  };

  return (
    <CommentSidebarProvider defaultOpen={true}>
      <SidebarInset style={{ overflow: 'hidden', height: APP_VIEWPORT_HEIGHT }}>
        {!isExportOpen && (
          <FormattingBar
            editor={editor}
            actions={['comment']}
            onCommentClick={() => {
              if (editor) {
                const { state } = editor;
                const { selection } = state;
                const { from, to } = selection;
                if (from !== to) {
                  const selectedText = getDocumentPlainText(state.doc, from, to);
                  setSelectedTextForComment(selectedText);
                  setSelectedRangeForComment({ from, to });
                  setIsCommentModalOpen(true);
                }
              }
            }}
          />
        )}
        <CommentModal
          isOpen={isCommentModalOpen}
          mentionableEntities={mentionableEntities}
          onClose={() => {
            const range = selectedRangeForComment;
            setIsCommentModalOpen(false);
            setSelectedTextForComment('');
            setSelectedRangeForComment(null);
            if (editor && range) {
              deferAfterDialogExit(() => {
                restoreEditorTextSelection(editor, range);
              });
            }
          }}
          onSubmit={async (content: string) => {
            if (!editor || !token || !documentId || documentId === 'new' || !selectedRangeForComment) {
              return;
            }
            setIsCreatingComment(true);
            try {
              const response = await fetch(`${API_BASE}/api/comments`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  documentId,
                  content,
                  fromPos: selectedRangeForComment.from,
                  toPos: selectedRangeForComment.to,
                  ...(encodeDocAnchor(
                    editor,
                    ydocRef.current,
                    selectedRangeForComment.from,
                    selectedRangeForComment.to,
                  ) ?? {}),
                  selectedText: selectedTextForComment,
                }),
              });
              if (response.ok) {
                const range = selectedRangeForComment;
                setIsCommentModalOpen(false);
                setSelectedTextForComment('');
                setSelectedRangeForComment(null);
                await waitForDialogExit();
                if (!editor.isDestroyed) {
                  restoreEditorTextSelection(editor, range);
                }
                await syncCommentLists();
              } else {
                showErrorToast(t('failed_to_create_comment'));
              }
            } catch (err) {
              console.error('Error creating comment:', err);
              showErrorToast(t('failed_to_create_comment'));
            } finally {
              setIsCreatingComment(false);
            }
          }}
          selectedText={selectedTextForComment}
          isLoading={isCreatingComment}
        />
        {composeSession && composeToolbarPosition && (
          <div
            ref={composeToolbarRef}
            className="fixed z-[1000] flex w-[min(28rem,calc(100vw-20px))] -translate-x-1/2 flex-col gap-2 rounded-md border border-border bg-card p-2 shadow-md"
            style={{ left: composeToolbarPosition.left, top: composeToolbarPosition.top }}
          >
            <div className="flex items-center gap-2">
              <span className="hidden min-w-0 flex-1 truncate px-1 text-xs text-muted-foreground sm:inline">
                {t('commenter_suggestion_compose_hint')}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelCompose}
                disabled={isCreatingSuggestion}
              >
                {t('cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  submitSuggestion();
                }}
                disabled={isCreatingSuggestion}
              >
                {isCreatingSuggestion ? t('saving') : t('commenter_suggestion_submit')}
              </Button>
            </div>
            {composeSession.mode === 'delete' ? (
              <TextDiff
                before={composeSession.originalText}
                variant="deletion"
                deleteLabel={t('suggestion_deletion')}
                className="!mt-0 !p-2 max-h-36 overflow-y-auto"
              />
            ) : (
              <TextDiff
                before={composeSession.originalText}
                after={composeDraftText}
                variant="inline"
                beforeLabel={t('suggestion_before')}
                afterLabel={t('suggestion_after')}
                className="!mt-0 !p-2 max-h-36 overflow-y-auto"
              />
            )}
          </div>
        )}
        <div style={{ display: 'flex', height: APP_VIEWPORT_HEIGHT, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row', position: 'relative' }}>
          <FloatingTocButton
            tocHeadings={tocHeadings}
            isFloatingTocVisible={isFloatingTocVisible}
            setIsFloatingTocVisible={setIsFloatingTocVisible}
            isTocPinned={isTocPinned}
            setIsTocPinned={setIsTocPinned}
            isMobile={isMobile}
            isFocusMode={false}
            hasMainAreaView={isExportOpen}
            t={t}
          />
          {!isExportOpen && (
            <FloatingToc
              tocHeadings={tocHeadings}
              tocParagraphs={tocParagraphs}
              isFloatingTocVisible={isFloatingTocVisible}
              setIsFloatingTocVisible={setIsFloatingTocVisible}
              isTocPinned={isTocPinned}
              setIsTocPinned={setIsTocPinned}
              activeTocPos={activeTocPos}
              isMobile={isMobile}
              isReadOnly={true}
              scrollToTocPosition={scrollToTocPosition}
              moveTocSection={moveTocSection}
              draggedTocIndex={draggedTocIndex}
              setDraggedTocIndex={setDraggedTocIndex}
              dragOverTocIndex={dragOverTocIndex}
              setDragOverTocIndex={setDragOverTocIndex}
              touchDraggedIndex={touchDraggedIndex}
              setTouchDraggedIndex={setTouchDraggedIndex}
              touchDragOverIndex={touchDragOverIndex}
              setTouchDragOverIndex={setTouchDragOverIndex}
              touchStartY={touchStartY}
              setTouchStartY={setTouchStartY}
              tocListRef={tocListRef}
              t={t}
            />
          )}
          <div style={{ flex: 1, overflow: 'auto', paddingTop: '0', paddingBottom: '2rem', position: 'relative' }}>
            <DocumentHeader
              documentTitle={documentTitle}
              projectTitle={projectTitle}
              documentIcon={documentIcon}
              onDocumentTitleChange={() => {}}
              onProjectTitleChange={() => {}}
              onIconChange={() => {}}
              isReadOnly={true}
              showFocusMode={false}
              ownerEmail={ownerEmail}
              hasCommentPermission={true}
            />
            {isExportOpen ? (
              <div style={{ maxWidth: '100%', margin: '0 auto', paddingLeft: '2rem', paddingRight: '2rem', paddingTop: '2rem' }}>
                <ExportDocument
                  editor={editor}
                  ydoc={null}
                  documentTitle={documentTitle}
                  projectTitle={projectTitle}
                  documentId={documentId}
                  token={token}
                  comments={comments}
                  suggestions={suggestions}
                  t={t}
                  hideHeading={false}
                  onClose={closeExport}
                />
              </div>
            ) : (
              <div style={{ maxWidth: '900px', margin: '0 auto', paddingLeft: '2rem', paddingRight: '2rem' }}>
                <div className="mt-6 rounded-lg border border-border bg-muted/30 p-4">
                  <div className="text-sm font-medium text-foreground">{t('commenter_suggestion_mode_title')}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{t('commenter_suggestion_mode_description')}</div>
                </div>
                <div style={{ minHeight: '400px', padding: '1rem 0' }}>
                  {editor && <EditorContent editor={editor} />}
                </div>
              </div>
            )}
          </div>
        </div>
        <CommentHoverCard
          editor={editor}
          hoveredComment={hoveredComment}
          hoveredCommentPosition={hoveredCommentPosition}
          commentHoverCardContentRef={commentHoverCardContentRef}
          isMobile={isMobile}
          documentOwnerId={documentOwnerId}
          onLoadComments={syncCommentLists}
          onClose={closeCommentHover}
          onHoverCardMouseEnter={onCommentHoverCardMouseEnter}
          onHoverCardMouseLeave={onCommentHoverCardMouseLeave}
          setKeepCommentHoverOpen={setKeepCommentHoverOpen}
          setHoveredComment={setHoveredComment}
          mentionableEntities={mentionableEntities}
        />
        <SuggestionHoverCard
          hoveredSuggestion={hoveredSuggestion}
          hoveredSuggestionPosition={hoveredSuggestionPosition}
          suggestionHoverCardContentRef={suggestionHoverCardContentRef}
          isMobile={isMobile}
          onClose={closeSuggestionHover}
          onHoverCardMouseEnter={onSuggestionHoverCardMouseEnter}
          onHoverCardMouseLeave={onSuggestionHoverCardMouseLeave}
          onDismissSuggestion={
            hoveredSuggestion && canDismissSuggestion(hoveredSuggestion)
              ? dismissSuggestion
              : undefined
          }
          t={t}
          getIssueTypeLabel={getIssueTypeLabel}
        />
      </SidebarInset>
      <CommentSidebar
        editor={editor}
        documentId={documentId}
        isReadOnly={false}
        comments={comments}
        onLoadComments={syncCommentLists}
        deletedComments={deletedComments}
        onLoadDeletedComments={syncCommentLists}
        showDeletedCommentsTab={true}
        documentOwnerId={documentOwnerId}
        suggestions={suggestions}
        onDismissSuggestion={dismissSuggestion}
        onScrollToSuggestion={scrollToSuggestion}
        canDismissSuggestion={canDismissSuggestion}
        getIssueTypeLabel={getIssueTypeLabel}
        t={t}
        showExport={true}
        isExportActive={isExportOpen}
        onExportClick={toggleExport}
        mentionableEntities={mentionableEntities}
      />
    </CommentSidebarProvider>
  );
};

